import {
  ApplyToLevel,
  ApplyToProductType,
  DiscountType,
  UserType,
} from "interfaces";
import { isEmpty } from "lodash";
import { canAccessByType } from "utills/helpers";
import { z } from "zod";

export const EditPromotionConditionSchema = (
  isShowPaymentMethod: boolean,
  isShowUsagePerAccount: boolean,
  isShowDailyActiveTime: boolean,
  eventCurrencies: string[]
) => {
  const ApplyToProductTypeEnum = z.enum([
    ApplyToProductType.ALL,
    ApplyToProductType.INCLUDE,
    ApplyToProductType.EXCLUDE,
  ]);

  const DiscountTypeDisplayEnum = z.enum([
    DiscountType.PERCENT,
    DiscountType.FIX_PRICE,
    DiscountType.QUANTITY_DISCOUNT,
    DiscountType.GROUP_DISCOUNT,
    DiscountType.CAMPAIGN_DISCOUNT,
  ]);

  const AmountSchema = z.preprocess(
    (val) => {
      if (val === "" || val === undefined || val === null) {
        return null;
      }
      return Number(val);
    },
    z
      .number({
        invalid_type_error: "Please input a number",
      })
      .nullable()
      .optional()
  );

  // Schema for quantity discount rules
  const quantityDiscountRulesSchema = z
    .object({
      qty: z
        .number({
          required_error: "Quantity is required",
          invalid_type_error: "Please input a number",
        })
        .int("Quantity must be an integer")
        .min(1, "Quantity must be at least 1"),
      discount_type: z.enum([DiscountType.PERCENT, DiscountType.FIX_PRICE], {
        required_error: "Discount condition is required",
      }),
      discount_value: z
        .union([
          z
            .number({
              required_error: "This field is required",
              invalid_type_error: "Please input a number",
            })
            .min(0)
            .max(100), // for percentage
          AmountSchema, // for fixed amount
        ])
        .optional(),
      discount_value_usd: AmountSchema,
      apply_to_level: z
        .string({ invalid_type_error: "This field is required" })
        .min(1, "This field is required"),

      max_discount_value: z.union([AmountSchema, z.nan()]).optional(),
      max_discount_value_usd: z.union([AmountSchema, z.nan()]).optional(),
    })
    .superRefine((data, ctx) => {
      if (data.discount_type === DiscountType.PERCENT) {
        if (data.discount_value === undefined || data.discount_value === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Discount value is required for Percentage discount types",
            path: ["discount_value"],
          });
        } else if (
          data.discount_type === DiscountType.PERCENT &&
          (Number(data.discount_value) < 0 || Number(data.discount_value) > 100)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Percentage value must be between 0 and 100",
            path: ["discount_value"],
          });
        }
      }
      if (data.discount_type === DiscountType.FIX_PRICE) {
        if (eventCurrencies.includes("usd")) {
          if (
            data.discount_value_usd === undefined ||
            data.discount_value_usd === null
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                "Discount value is required for Fixed Amount discount types",
              path: ["discount_value_usd"],
            });
          } else if (Number(data.discount_value_usd) < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Amount value must be equal or greater than 0",
              path: ["discount_value_usd"],
            });
          }
        }
        if (eventCurrencies.includes("vnd")) {
          if (
            data.discount_value === undefined ||
            data.discount_value === null
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                "Discount value is required for Fixed Amount discount types",
              path: ["discount_value"],
            });
          } else if (Number(data.discount_value) < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Amount value must be equal or greater than 0",
              path: ["discount_value"],
            });
          }
        }
      }
    });

  return z
    .object({
      is_active: z.boolean(),
      apply_to_product_type: ApplyToProductTypeEnum,
      note: z
        .string()
        .min(1, "This field is required")
        .max(255, "This field must be less than 255 characters"),
      apply_from: z.string().min(1, "This field is required"),
      apply_to: z.string().min(1, "This field is required"),
      event_ids: z.array(z.string()).optional(),
      apply_product_ids: z.array(z.string()).optional(),
      discount_type_display: DiscountTypeDisplayEnum,
      discount_value: z
        .union([
          z
            .number({
              required_error: "This field is required",
              invalid_type_error: "Please input a number",
            })
            .min(0)
            .max(100), // for percentage
          AmountSchema, // for fixed amount
        ])
        .optional(),
      discount_value_usd: AmountSchema,

      // Max Discount Amount - only for percentage type
      max_discount_value: z.union([AmountSchema, z.nan()]).optional(),
      max_discount_value_usd: z.union([AmountSchema, z.nan()]).optional(),

      // Apply To Level field - only for percentage, fixed amount, campaign/special discount
      apply_to_level: z.string().nullable().optional(),

      // Min Order Value - only when apply_to_level is total_order
      min_total: z.union([AmountSchema, z.nan()]).optional(),
      min_total_usd: z.union([AmountSchema, z.nan()]).optional(),

      // Max Applicable Items - only when apply_to_level is per_item
      apply_max_item_on_cart_qty: z.union([AmountSchema, z.nan()]).optional(),

      // Group Discount Rules - only for GROUP_DISCOUNT type
      quantity_discount_rules: z.array(quantityDiscountRulesSchema).optional(),

      // Campaign Discount Rules - only for CAMPAIGN_DISCOUNT type
      campaign_code: z.string().nullable().optional(),
      discount_type: z
        .enum([
          DiscountType.PERCENT,
          DiscountType.FIX_PRICE,
          DiscountType.QUANTITY_DISCOUNT,
          DiscountType.GROUP_DISCOUNT,
          DiscountType.CAMPAIGN_DISCOUNT,
        ])
        .optional(),

      // Additional Setup
      apply_to_payment_methods: z.array(z.string()),
      max_times_used_per_account: AmountSchema.optional(),
      apply_from_hour: z.string().nullable().optional(),
      apply_to_hour: z.string().nullable().optional(),
      max_times_used_per_day: AmountSchema.optional(),
      special_discount: z.boolean().optional(),
      coupon_code: z.string().nullable().optional(),
      customer_code: z.string().nullable().optional(),
      ...(canAccessByType(UserType.ADMIN) && {
        max_times_used: z
          .number({
            invalid_type_error: "Please input a number",
          })
          .int()
          .min(1, "Max times used must be at least 1"),
      }),
    })
    .superRefine((data, ctx) => {
      // 1. Validate event_ids and apply_product_ids
      if (
        data.apply_to_product_type === ApplyToProductType.INCLUDE ||
        data.apply_to_product_type === ApplyToProductType.EXCLUDE
      ) {
        if (!data.event_ids || data.event_ids.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field is required",
            path: ["event_ids"],
          });
        }
        if (!data.apply_product_ids || data.apply_product_ids.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field is required",
            path: ["apply_product_ids"],
          });
        }
      }

      // 2. Validate date range
      if (data.apply_to < data.apply_from) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Apply To cannot be earlier than Apply From",
          path: ["apply_to"],
        });
      }

      // 3. Validate discount value requirements
      const discountType =
        data.discount_type_display === DiscountType.CAMPAIGN_DISCOUNT
          ? data.discount_type
          : data.discount_type_display;
      if (discountType === DiscountType.PERCENT) {
        if (data.discount_value === undefined || data.discount_value === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Discount value is required for Percentage discount types",
            path: ["discount_value"],
          });
        } else if (
          discountType === DiscountType.PERCENT &&
          (Number(data.discount_value) < 0 || Number(data.discount_value) > 100)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Percentage value must be between 0 and 100",
            path: ["discount_value"],
          });
        }
        if (data.max_discount_value_usd) {
          if (Number(data.max_discount_value_usd) < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Value must be greater than or equal to 0",
              path: ["max_discount_value_usd"],
            });
          }
        }
        if (data.max_discount_value) {
          if (Number(data.max_discount_value) < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Value must be greater than or equal to 0",
              path: ["max_discount_value"],
            });
          }
        }
      }
      if (discountType === DiscountType.FIX_PRICE) {
        if (eventCurrencies.includes("usd")) {
          if (
            data.discount_value_usd === undefined ||
            data.discount_value_usd === null
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                "Discount value is required for Fixed Amount discount types",
              path: ["discount_value_usd"],
            });
          } else if (Number(data.discount_value_usd) < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Amount value must be equal or greater than 0",
              path: ["discount_value_usd"],
            });
          }
        }
        if (eventCurrencies.includes("vnd")) {
          if (
            data.discount_value === undefined ||
            data.discount_value === null
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                "Discount value is required for Fixed Amount discount types",
              path: ["discount_value"],
            });
          } else if (Number(data.discount_value) < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Amount value must be equal or greater than 0",
              path: ["discount_value"],
            });
          }
        }
      }

      // 4. Validate apply_to_level requirements
      const requiresApplyToLevel = [
        DiscountType.PERCENT,
        DiscountType.FIX_PRICE,
        DiscountType.CAMPAIGN_DISCOUNT,
      ];
      if (
        requiresApplyToLevel.includes(data.discount_type_display) &&
        isEmpty(data.apply_to_level)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "This field is required",
          path: ["apply_to_level"],
        });
      }

      // 5. Validate min total
      if (data.apply_to_level === ApplyToLevel.TOTAL_ORDER) {
        if (data.min_total) {
          if (Number(data.min_total) < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Value must be greater than or equal to 0",
              path: ["min_total"],
            });
          }
        }
        if (data.min_total_usd) {
          if (Number(data.min_total_usd) < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Value must be greater than or equal to 0",
              path: ["min_total_usd"],
            });
          }
        }
      }

      // 6. Validate apply_max_item_on_cart_qty
      if (data.apply_to_level === ApplyToLevel.PER_ITEM) {
        if (
          data.apply_max_item_on_cart_qty === undefined ||
          data.apply_max_item_on_cart_qty === null
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Max Applicable Items is required when Apply To Level is Per Item",
            path: ["apply_max_item_on_cart_qty"],
          });
        } else if (Number(data.apply_max_item_on_cart_qty) < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Value must be greater than or equal to 1",
            path: ["apply_max_item_on_cart_qty"],
          });
        }
      }

      // 7. Validate quantity_discount_rules requirements
      if (data.discount_type_display === DiscountType.GROUP_DISCOUNT) {
        if (
          !data.quantity_discount_rules ||
          data.quantity_discount_rules.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "At least one discount condition is required for Group Discount",
            path: ["quantity_discount_rules"],
          });
        }
      }

      // 10. Validate campaign section requirements
      if (data.discount_type_display === DiscountType.CAMPAIGN_DISCOUNT) {
        if (!data.discount_type) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field is required",
            path: ["discount_type"],
          });
        }
        if (!data.campaign_code) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field is required",
            path: ["campaign_code"],
          });
        } else if (data.campaign_code.length > 32) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Text length is too long",
            path: ["campaign_code"],
          });
        }
      }

      // 11. Validate additional setup
      const discountTypeForPaymentMethods = [
        DiscountType.FIX_PRICE,
        DiscountType.PERCENT,
        DiscountType.QUANTITY_DISCOUNT,
        DiscountType.CAMPAIGN_DISCOUNT,
      ];
      if (
        isShowPaymentMethod &&
        discountTypeForPaymentMethods.includes(data.discount_type_display)
      ) {
        if (
          !data.apply_to_payment_methods ||
          data.apply_to_payment_methods.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This field is required",
            path: ["apply_to_payment_methods"],
          });
        }
      }

      const discountTypeForUsagePerAccount = [
        DiscountType.FIX_PRICE,
        DiscountType.PERCENT,
      ];

      if (
        isShowUsagePerAccount &&
        discountTypeForUsagePerAccount.includes(data.discount_type_display)
      ) {
        if (
          (data.max_times_used_per_account === null &&
            data.max_times_used_per_account === "") ||
          data.max_times_used_per_account === undefined
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This fields is required",
            path: ["max_times_used_per_account"],
          });
        }
      }

      const discountTypeForDailyActiveTime = [
        DiscountType.FIX_PRICE,
        DiscountType.PERCENT,
      ];

      if (
        isShowDailyActiveTime &&
        discountTypeForDailyActiveTime.includes(data.discount_type_display)
      ) {
        if (data.apply_from_hour && data.apply_to_hour) {
          if (data.apply_to_hour < data.apply_from_hour) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Apply To Hour cannot be earlier than Apply From Hour",
              path: ["apply_to_hour"],
            });
          }
        } else {
          if (data.apply_from_hour === "") {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "This fields is required",
              path: ["apply_from_hour"],
            });
          }
          if (data.apply_to_hour === "") {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "This fields is required",
              path: ["apply_to_hour"],
            });
          }
        }
        if (data.max_times_used_per_day) {
          if (Number(data.max_times_used_per_day) < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Value must be euqal or greater than 0",
              path: ["max_times_used_per_day"],
            });
          } else if (!Number.isInteger(Number(data.max_times_used_per_day))) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Value must be an integer number",
              path: ["max_times_used_per_day"],
            });
          }
        }
      }

      const discountTypeForSpecialDiscount = [DiscountType.GROUP_DISCOUNT];

      if (
        data.special_discount &&
        discountTypeForSpecialDiscount.includes(data.discount_type_display)
      ) {
        if (!data.coupon_code) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This fields is required",
            path: ["coupon_code"],
          });
        } else if (data.coupon_code?.length > 20) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Maximum coupon code length is 20",
            path: ["coupon_code"],
          });
        }
        if (data.customer_code === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "This fields is required",
            path: ["customer_code"],
          });
        }
      }
    });
};
